/**
 * oacService.js ,  SenritsuX Official Artist Channel (OAC) Service
 * 
 * Manages OAC request lifecycle, SenritsuX internal screening eligibility checks,
 * YouTube link validation, and artist request submissions.
 *
 * IMPORTANT DISTINCTION:
 * - YouTube Platform Requirements: Own/operate channel representing artist, at least 1 official music release delivered by distributor/partner, policy compliance.
 * - SenritsuX Internal Screening Requirements: At least 1 SenritsuX-distributed official release with YouTube Topic link, and at least 3 public non-Shorts videos on the artist's channel.
 */

import { supabase, isSupabaseConfigured } from './supabase.js';

export const OAC_STATUS = {
  PENDING: 'pending',
  UNDER_REVIEW: 'under_review',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
};

export const OAC_STATUS_LABELS = {
  [OAC_STATUS.PENDING]: 'Under Review',
  [OAC_STATUS.UNDER_REVIEW]: 'Under Review',
  [OAC_STATUS.APPROVED]: 'Approved / Processed',
  [OAC_STATUS.REJECTED]: 'Needs Attention',
  [OAC_STATUS.CANCELLED]: 'Cancelled',
};

/**
 * SenritsuX Internal Requirements Copy
 */
export const SENRITSUX_OAC_DISCLAIMERS = {
  THREE_VIDEO_NOTE: 'Your YouTube channel must have at least 3 public videos. Shorts do not count toward this SenritsuX screening requirement.',
  SCREENING_VS_YOUTUBE: "These are SenritsuX's internal screening requirements. Final OAC eligibility, verification, and approval are subject to YouTube policies and the applicable distributor delivery process.",
  OFFICIAL_RELEASE_REQUIRED: 'Your OAC request becomes available after you have an official release distributed through SenritsuX and meet the required channel criteria.',
};

/**
 * Validate a YouTube Topic / Art Track URL.
 * Accepts:
 *   - https://www.youtube.com/watch?v=VIDEO_ID
 *   - https://youtube.com/watch?v=VIDEO_ID
 *   - https://youtu.be/VIDEO_ID
 *   - https://music.youtube.com/watch?v=VIDEO_ID
 */
export function validateYouTubeTopicUrl(input) {
  if (!input || typeof input !== 'string') {
    return { valid: false, error: 'Please enter your YouTube Topic or official release link.' };
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return { valid: false, error: 'Please enter your YouTube Topic or official release link.' };
  }

  try {
    const urlString = trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? trimmed
      : `https://${trimmed}`;

    const parsed = new URL(urlString);
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');

    const isYouTubeHost =
      hostname === 'youtube.com' ||
      hostname === 'music.youtube.com' ||
      hostname === 'youtu.be';

    if (!isYouTubeHost) {
      return { valid: false, error: 'Please enter a valid YouTube release or Topic URL.' };
    }

    if (hostname === 'youtu.be') {
      const videoId = parsed.pathname.slice(1).split('/')[0];
      if (!videoId || videoId.length < 5) {
        return { valid: false, error: 'Invalid YouTube shortlink video ID.' };
      }
      return { valid: true, normalizedUrl: `https://www.youtube.com/watch?v=${videoId}` };
    }

    // For youtube.com or music.youtube.com
    const vParam = parsed.searchParams.get('v');
    if (vParam) {
      return { valid: true, normalizedUrl: `https://www.youtube.com/watch?v=${vParam}` };
    }

    // Path might be /watch/VIDEO_ID or /v/VIDEO_ID
    if (parsed.pathname.startsWith('/watch')) {
      const vid = parsed.pathname.split('/watch/')[1]?.split('/')[0];
      if (vid) {
        return { valid: true, normalizedUrl: `https://www.youtube.com/watch?v=${vid}` };
      }
    }

    // Direct /embed/ or /shorts/
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    if (pathParts.length >= 2 && (pathParts[0] === 'v' || pathParts[0] === 'embed')) {
      return { valid: true, normalizedUrl: `https://www.youtube.com/watch?v=${pathParts[1]}` };
    }

    return { valid: false, error: 'Please provide a direct YouTube video or Topic track link with a valid video ID.' };
  } catch {
    return { valid: false, error: 'Invalid URL format.' };
  }
}

/**
 * Validate a YouTube Channel URL.
 * Supports:
 *   - https://www.youtube.com/@handle
 *   - https://youtube.com/@handle
 *   - @handle
 *   - https://www.youtube.com/channel/UC...
 *   - https://www.youtube.com/c/CustomName
 *   - https://www.youtube.com/user/Username
 */
export function validateYouTubeChannelUrl(input) {
  if (!input || typeof input !== 'string') {
    return { valid: false, error: 'Please enter your official YouTube Channel link.' };
  }

  const trimmed = input.trim();
  if (!trimmed) {
    return { valid: false, error: 'Please enter your official YouTube Channel link.' };
  }

  // Handle @handle shorthand
  if (trimmed.startsWith('@')) {
    const handle = trimmed.slice(1).trim();
    if (!handle || /[^a-zA-Z0-9._-]/.test(handle)) {
      return { valid: false, error: 'Invalid YouTube handle format.' };
    }
    return { valid: true, normalizedUrl: `https://www.youtube.com/@${handle}` };
  }

  try {
    const urlString = trimmed.startsWith('http://') || trimmed.startsWith('https://')
      ? trimmed
      : `https://${trimmed}`;

    const parsed = new URL(urlString);
    const hostname = parsed.hostname.toLowerCase().replace(/^www\./, '');

    if (hostname !== 'youtube.com') {
      return { valid: false, error: 'Please enter a valid YouTube channel URL (youtube.com/@handle or channel link).' };
    }

    const pathname = parsed.pathname;

    // Handle @username
    if (pathname.startsWith('/@')) {
      const handle = pathname.slice(2).split('/')[0].trim();
      if (!handle) {
        return { valid: false, error: 'Invalid YouTube channel handle.' };
      }
      return { valid: true, normalizedUrl: `https://www.youtube.com/@${handle}` };
    }

    // Handle /channel/UC...
    if (pathname.startsWith('/channel/')) {
      const channelId = pathname.slice('/channel/'.length).split('/')[0].trim();
      if (!channelId || !channelId.startsWith('UC')) {
        return { valid: false, error: 'Channel ID must begin with "UC".' };
      }
      return { valid: true, normalizedUrl: `https://www.youtube.com/channel/${channelId}` };
    }

    // Handle /c/CustomName
    if (pathname.startsWith('/c/')) {
      const customName = pathname.slice('/c/'.length).split('/')[0].trim();
      if (!customName) {
        return { valid: false, error: 'Invalid custom channel URL.' };
      }
      return { valid: true, normalizedUrl: `https://www.youtube.com/c/${customName}` };
    }

    // Handle /user/Username
    if (pathname.startsWith('/user/')) {
      const username = pathname.slice('/user/'.length).split('/')[0].trim();
      if (!username) {
        return { valid: false, error: 'Invalid YouTube username URL.' };
      }
      return { valid: true, normalizedUrl: `https://www.youtube.com/user/${username}` };
    }

    return {
      valid: false,
      error: 'Please enter a valid YouTube channel format (e.g. youtube.com/@yourname or youtube.com/channel/UC...).',
    };
  } catch {
    return { valid: false, error: 'Invalid channel URL format.' };
  }
}

/**
 * Fetch qualifying official releases distributed through SenritsuX for an artist.
 * Qualifying status includes: submitted, under_review, approved, delivery, delivered, live.
 * Drafts are explicitly excluded.
 */
export async function fetchArtistQualifyingReleases(userId) {
  if (!userId || !isSupabaseConfigured()) {
    return { data: [], error: null };
  }

  try {
    const { data, error } = await supabase
      .from('releases')
      .select('id, title, release_type, genre, status, senritsux_registration_number, cover_url, release_date, created_at')
      .eq('user_id', userId)
      .in('status', ['submitted', 'under_review', 'approved', 'delivery', 'delivered', 'live'])
      .order('created_at', { ascending: false });

    if (error) {
      console.warn('[oacService] Notice fetching qualifying releases:', error.message);
      return { data: [], error };
    }

    return { data: data || [], error: null };
  } catch (err) {
    console.error('[oacService] Exception in fetchArtistQualifyingReleases:', err);
    return { data: [], error: err };
  }
}

/**
 * Fetch the latest OAC request for a user.
 */
export async function fetchUserOacRequest(userId) {
  if (!userId || !isSupabaseConfigured()) {
    return { data: null, error: null };
  }

  try {
    const { data, error } = await supabase
      .from('oac_requests')
      .select(`
        id,
        user_id,
        artist_profile_id,
        release_id,
        youtube_release_url,
        youtube_channel_url,
        status,
        review_notes,
        rejection_reason,
        submitted_at,
        updated_at,
        reviewed_at,
        releases (
          id,
          title,
          release_type,
          senritsux_registration_number,
          status,
          cover_url
        )
      `)
      .eq('user_id', userId)
      .order('submitted_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      // If table does not exist yet (code 42P01), fail gracefully
      if (error.code === '42P01') {
        return { data: null, error: null };
      }
      console.warn('[oacService] Error fetching user OAC request:', error.message);
      return { data: null, error };
    }

    return { data: data || null, error: null };
  } catch (err) {
    console.error('[oacService] Exception in fetchUserOacRequest:', err);
    return { data: null, error: err };
  }
}

/**
 * Submit an Official Artist Channel Request
 */
export async function submitOacRequest({
  userId,
  artistProfileId,
  releaseId,
  youtubeReleaseUrl,
  youtubeChannelUrl,
  checklistConfirmed = false,
}) {
  if (!userId) {
    throw new Error('Authentication required to submit an OAC request.');
  }

  if (!checklistConfirmed) {
    throw new Error('Please review and confirm all checklist requirements before submitting.');
  }

  if (!releaseId) {
    throw new Error('Please select an official SenritsuX-distributed release.');
  }

  const topicValidation = validateYouTubeTopicUrl(youtubeReleaseUrl);
  if (!topicValidation.valid) {
    throw new Error(topicValidation.error);
  }

  const channelValidation = validateYouTubeChannelUrl(youtubeChannelUrl);
  if (!channelValidation.valid) {
    throw new Error(channelValidation.error);
  }

  if (!isSupabaseConfigured()) {
    throw new Error('Database connection is not configured.');
  }

  // 1. Verify release ownership & eligibility
  const { data: releaseRow, error: relErr } = await supabase
    .from('releases')
    .select('id, user_id, title, status')
    .eq('id', releaseId)
    .eq('user_id', userId)
    .maybeSingle();

  if (relErr || !releaseRow) {
    throw new Error('Selected release was not found or does not belong to your account.');
  }

  if (releaseRow.status === 'draft') {
    throw new Error('A draft release cannot be used for an OAC request. The release must be distributed through SenritsuX.');
  }

  // 2. Prevent duplicate active requests
  const { data: existingActive, error: activeErr } = await supabase
    .from('oac_requests')
    .select('id, status')
    .eq('user_id', userId)
    .in('status', ['pending', 'under_review'])
    .maybeSingle();

  if (activeErr && activeErr.code !== '42P01' && activeErr.code !== 'PGRST116') {
    console.warn('[oacService] Active request check warning:', activeErr.message);
  }

  if (existingActive) {
    throw new Error('You already have an OAC request currently under review. Our team is processing it.');
  }

  // 3. Insert new OAC request
  const payload = {
    user_id: userId,
    artist_profile_id: artistProfileId || null,
    release_id: releaseId,
    youtube_release_url: topicValidation.normalizedUrl,
    youtube_channel_url: channelValidation.normalizedUrl,
    status: 'pending',
    submitted_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const { data: inserted, error: insertError } = await supabase
    .from('oac_requests')
    .insert(payload)
    .select()
    .single();

  if (insertError) {
    console.error('[oacService] Error inserting OAC request:', insertError);
    if (insertError.code === '23505') {
      throw new Error('You already have an active OAC request under review.');
    }
    throw new Error(insertError.message || 'Unable to submit OAC request. Please try again.');
  }

  // 4. Send notification to artist
  try {
    await supabase.from('notifications').insert({
      user_id: userId,
      title: 'OAC Request Submitted',
      message: `Your Official Artist Channel request for "${releaseRow.title}" has been received and is under review by the SenritsuX distribution team.`,
      type: 'distribution',
      data: {
        oac_request_id: inserted.id,
        release_id: releaseId,
      },
    });
  } catch (notifErr) {
    console.warn('[oacService] Failed to send submission notification:', notifErr);
  }

  return { success: true, data: inserted };
}

/**
 * Cancel a pending OAC request
 */
export async function cancelOacRequest(requestId, userId) {
  if (!requestId || !userId || !isSupabaseConfigured()) {
    throw new Error('Request ID and User ID are required to cancel.');
  }

  const { data, error } = await supabase
    .from('oac_requests')
    .update({
      status: 'cancelled',
      updated_at: new Date().toISOString(),
    })
    .eq('id', requestId)
    .eq('user_id', userId)
    .eq('status', 'pending')
    .select()
    .single();

  if (error) {
    throw new Error(error.message || 'Unable to cancel request.');
  }

  return { success: true, data };
}
